import { styled } from '@mui/material/styles';
import ArticleBox from '@buildwise/pages/knowledge/components/ArticleBox';
import { useProjectRetroContext } from '../../context';
import { Box, IconButton, Tabs } from '@mui/material';
import CloseLineIcon from 'remixicon-react/CloseLineIcon';
import { useEffect, useRef, useState } from 'react';
import { Tab } from '@mui/material';
import { ReactComponent as RetrospectiveLineIcon } from 'assets/icons/RetrospectiveLineIcon.svg';
import { ReactComponent as RetrospectiveFillIcon } from 'assets/icons/RetrospectiveFillIcon.svg';
import Chat3FillIcon from 'remixicon-react/Chat3FillIcon';
import Chat3LineIcon from 'remixicon-react/Chat3LineIcon';
import useArticleDetailsQuery, {
  useArticleCommentsQuery,
} from '@buildwise/pages/article/hooks/useArticleDetailsQuery';
import { useParams } from 'react-router-dom';
import useProjectDetailsQuery from '@buildwise/pages/project/hooks/useProjectDetailsQuery';
import { useArticleInputFields } from '@buildwise/libs/posts';
import { ArticleFieldsType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';

function ViewLearning() {
  const {
    state: { selectedArticle },
    dispatch,
  } = useProjectRetroContext();
  const { articleFieldsToRender } = useArticleInputFields({
    articleFieldsType: selectedArticle?.articleType as unknown as ArticleFieldsType,
    isEditing: false,
  });
  const { articleResponse } = useArticleDetailsQuery({
    articleId: selectedArticle?.id || '',
    updateViewCount: true,
    shouldFetch: false,
  });
  const [tab, setTab] = useState('learning');
  const { comments } = useArticleCommentsQuery({
    articleId: selectedArticle?.id || '',
  });
  const learningContainerRef = useRef<HTMLDivElement | null>(null);
  const { projectId = '' } = useParams();
  const { refetch } = useProjectDetailsQuery({
    projectId,
    shouldFetch: false,
  });

  useEffect(() => {
    if (!learningContainerRef.current) return;

    const commentsBox = learningContainerRef.current.querySelector('.article-comments');
    if (!commentsBox) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (learningContainerRef.current) {
          const isScrollable =
            learningContainerRef.current.scrollHeight > learningContainerRef.current.clientHeight;
          if (isScrollable) {
            setTab(entry.isIntersecting ? 'comments' : 'learning');
          }
        }
      },
      { root: learningContainerRef.current, threshold: 0.5 }, // 50% visibility needed
    );

    observer.observe(commentsBox);

    return () => observer.disconnect();
  }, [learningContainerRef.current]);

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Tabs
        value={tab}
        onChange={(_: unknown, newValue: string) => {
          setTab(newValue);

          // Scroll to comments
          if (newValue === 'comments' && learningContainerRef.current) {
            const commentsEle = learningContainerRef.current.getElementsByClassName(
              'article-comments',
            )?.[0] as HTMLDivElement;
            if (commentsEle) {
              commentsEle.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          } else {
            // Scroll to top
            learningContainerRef.current?.scrollTo({
              top: 0,
              left: 0,
              behavior: 'smooth',
            });
          }
        }}
        sx={{ px: 1, pt: 1 }}
      >
        <Tab
          icon={
            tab === 'learning' ? (
              <RetrospectiveFillIcon width={18} />
            ) : (
              <RetrospectiveLineIcon width={18} />
            )
          }
          iconPosition="start"
          key="learning"
          value="learning"
          label="Learning"
        />
        <Tab
          icon={tab === 'comments' ? <Chat3FillIcon size={18} /> : <Chat3LineIcon size={18} />}
          iconPosition="start"
          key="comments"
          value="comments"
          label={`Comments ${comments.length ? `(${comments.length})` : ''}`}
        />
        <IconButton
          size="small"
          sx={{ position: 'absolute', right: 0 }}
          onClick={() => {
            dispatch({
              payload: {
                selectedArticle: undefined,
              },
            });
          }}
        >
          <CloseLineIcon />
        </IconButton>
      </Tabs>

      <Box flex={1} overflow="auto" ref={learningContainerRef} pb={6}>
        {selectedArticle && (
          <ArticleBox
            article={articleResponse?.companyArticle?.article || selectedArticle}
            variant="detailed"
            comments={comments}
            articleFields={articleFieldsToRender}
            sx={{ boxShadow: 'none' }}
            contentSx={{ padding: '0 16px !important' }}
            likeUpdateCallback={() => refetch()}
            bookmarkUpdateCallback={() => refetch()}
            endorseArticleCallback={() => refetch()}
            deleteArticleCallback={() => {
              refetch();
              dispatch({
                payload: {
                  selectedArticle: undefined,
                },
              });
            }}
            onEditArticleCallback={() =>
              dispatch({
                payload: {
                  isOpenedLessonForm: true,
                },
              })
            }
          />
        )}
      </Box>
    </Box>
  );
}

export const StickyTabsContainer = styled(Box, { label: 'StickyTabsContainer' })(({ theme }) => ({
  position: 'sticky',
  top: 0,
  backgroundColor: theme.palette.custom.white,
  zIndex: 1,
  width: '100%',
  marginBottom: 8,
}));

export default ViewLearning;
